/**
 * Meta (Facebook) Pixel helpers for UpClinic.
 *
 * - Never send patient data or PHI to Meta.
 * - At most send non-PHI params such as signup method.
 * - Pixel ID: REACT_APP_META_PIXEL_ID (default matches the landing-page pixel).
 */

export const META_PIXEL_ID =
  process.env.REACT_APP_META_PIXEL_ID || '646948901744249';

export type MetaSignupMethod = 'email' | 'google' | 'facebook' | 'microsoft' | 'social';

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
    _fbq?: (...args: unknown[]) => void;
  }
}

const COMPLETE_REGISTRATION_KEY_PREFIX = 'meta_pixel_complete_registration_';
const START_TRIAL_KEY_PREFIX = 'meta_pixel_start_trial_';

function canUseBrowserApis(): boolean {
  return typeof window !== 'undefined';
}

function safeLocalStorageGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeLocalStorageSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* ignore quota / private mode */
  }
}

function hasFiredOnce(prefix: string, dedupeKey?: string): boolean {
  if (!dedupeKey || !canUseBrowserApis()) return false;
  return safeLocalStorageGet(`${prefix}${dedupeKey}`) === '1';
}

function markFiredOnce(prefix: string, dedupeKey?: string): void {
  if (!dedupeKey || !canUseBrowserApis()) return;
  safeLocalStorageSet(`${prefix}${dedupeKey}`, '1');
}

/**
 * Ensures the standard Meta Pixel base code is loaded and initialized.
 * Safe to call multiple times; no-ops when already present (e.g. index.html stub).
 */
export function initMetaPixel(pixelId: string = META_PIXEL_ID): void {
  if (!canUseBrowserApis() || !pixelId) return;

  try {
    if (!window.fbq) {
      const fbq = function (...args: unknown[]) {
        const self = fbq as typeof fbq & {
          callMethod?: (...a: unknown[]) => void;
          queue: unknown[];
        };
        if (self.callMethod) {
          self.callMethod(...args);
        } else {
          self.queue.push(args);
        }
      } as ((...args: unknown[]) => void) & {
        queue: unknown[];
        push: (...args: unknown[]) => void;
        loaded: boolean;
        version: string;
        callMethod?: (...args: unknown[]) => void;
      };

      fbq.queue = [];
      fbq.push = fbq;
      fbq.loaded = true;
      fbq.version = '2.0';
      window.fbq = fbq;
      if (!window._fbq) window._fbq = fbq;

      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://connect.facebook.net/en_US/fbevents.js';
      const first = document.getElementsByTagName('script')[0];
      first?.parentNode?.insertBefore(script, first);
    }

    window.fbq?.('init', pixelId);
  } catch {
    /* never break auth/app flows */
  }
}

function track(
  eventName: string,
  eventData?: Record<string, unknown>,
  options?: { eventID?: string }
): void {
  if (!canUseBrowserApis()) return;
  try {
    if (!window.fbq) {
      initMetaPixel();
    }
    if (options?.eventID) {
      window.fbq?.('track', eventName, eventData || {}, { eventID: options.eventID });
    } else if (eventData && Object.keys(eventData).length > 0) {
      window.fbq?.('track', eventName, eventData);
    } else {
      window.fbq?.('track', eventName);
    }
  } catch {
    /* never break auth/app flows */
  }
}

/** SPA PageView — call on React Router location changes. */
export function trackPageView(): void {
  track('PageView');
}

/**
 * Fire CompleteRegistration exactly once per new account (localStorage dedupe).
 * Ordinary logins must not call this.
 * - eventID = Firebase uid (dedupe / CAPI match)
 * - content_name = utm_content (or signup method fallback) — never email/name/PHI
 */
export function trackCompleteRegistration(
  userKey?: string,
  method?: MetaSignupMethod,
  contentName?: string
): void {
  if (userKey && hasFiredOnce(COMPLETE_REGISTRATION_KEY_PREFIX, userKey)) {
    return;
  }

  // Brief: eventID = uid for Meta Events Manager matching
  const eventID = userKey || (canUseBrowserApis() ? crypto.randomUUID() : undefined);
  const params: Record<string, unknown> = { status: true };
  const name = contentName || method;
  if (name) {
    params.content_name = name;
  }

  track('CompleteRegistration', params, eventID ? { eventID } : undefined);

  if (userKey) {
    markFiredOnce(COMPLETE_REGISTRATION_KEY_PREFIX, userKey);
  }
}

/** StartTrial once when a new trial document is actually created. */
export function trackStartTrial(userKey?: string): void {
  if (userKey && hasFiredOnce(START_TRIAL_KEY_PREFIX, userKey)) {
    return;
  }

  track('StartTrial', { value: 0, currency: 'BRL' });

  if (userKey) {
    markFiredOnce(START_TRIAL_KEY_PREFIX, userKey);
  }
}

/** Optional Lead event helper (non-PHI). */
export function trackLead(eventId?: string): void {
  const id = eventId || (canUseBrowserApis() ? crypto.randomUUID() : undefined);
  track('Lead', {}, id ? { eventID: id } : undefined);
}
